/**
 * CookieBanner – DSGVO-konform
 * Erscheint beim ersten Besuch, speichert Entscheidung in localStorage.
 * Kategorien: Notwendig (immer), Statistik (Umami Analytics), Komfort (Google Fonts)
 */
import { useState, useEffect } from "react";
import { Link } from "wouter";
import { X, Cookie, ChevronDown, ChevronUp } from "lucide-react";

export type CookieConsent = {
  necessary: true;
  statistics: boolean;
  comfort: boolean;
  timestamp: number;
};

const STORAGE_KEY = "immozwerg_cookie_consent";
const COOKIE_NAME = "immozwerg_cookie_consent";
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const UMAMI_SCRIPT_ID = "immozwerg-umami";

function loadAnalytics() {
  if (typeof window === "undefined" || document.getElementById(UMAMI_SCRIPT_ID)) return;
  const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT?.replace(/\/$/, "");
  const websiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;
  if (!endpoint || !websiteId) return;

  const script = document.createElement("script");
  script.id = UMAMI_SCRIPT_ID;
  script.defer = true;
  script.src = `${endpoint}/umami`;
  script.dataset.websiteId = websiteId;
  document.head.appendChild(script);
}

function parseConsent(raw: string | null): CookieConsent | null {
  if (!raw) return null;
  try {
    const consent = JSON.parse(raw) as CookieConsent;
    if (consent.necessary !== true || typeof consent.statistics !== "boolean" || typeof consent.comfort !== "boolean" || typeof consent.timestamp !== "number") return null;
    return consent;
  } catch {
    return null;
  }
}

function getConsentCookie(): CookieConsent | null {
  if (typeof document === "undefined") return null;
  const rawCookie = document.cookie.split("; ").find((entry) => entry.startsWith(`${COOKIE_NAME}=`));
  if (!rawCookie) return null;
  try {
    return parseConsent(decodeURIComponent(rawCookie.slice(COOKIE_NAME.length + 1)));
  } catch {
    return null;
  }
}

export function getCookieConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const fromStorage = parseConsent(localStorage.getItem(STORAGE_KEY));
    if (fromStorage) return fromStorage;
  } catch {
    // Fallback auf den First-Party-Cookie, falls localStorage nicht verfügbar ist.
  }
  const fromCookie = getConsentCookie();
  if (fromCookie) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fromCookie));
    } catch {
      // Der Cookie bleibt die persistente Quelle, wenn localStorage gesperrt ist.
    }
  }
  return fromCookie;
}

function saveConsent(statistics: boolean, comfort: boolean) {
  const consent: CookieConsent = {
    necessary: true,
    statistics,
    comfort,
    timestamp: Date.now(),
  };
  const serializedConsent = JSON.stringify(consent);
  try {
    localStorage.setItem(STORAGE_KEY, serializedConsent);
  } catch {
    // Der First-Party-Cookie darunter sichert die Auswahl auch ohne localStorage.
  }
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(serializedConsent)}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
  // Analytics nur nach ausdrücklicher Statistik-Einwilligung laden.
  if (statistics) {
    (window as any).__umami_disabled = false;
    loadAnalytics();
  } else {
    (window as any).__umami_disabled = true;
  }
  return consent;
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [statistics, setStatistics] = useState(true);
  const [comfort, setComfort] = useState(true);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      // Kurze Verzögerung damit die Seite zuerst lädt
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
    // Bereits abgelehnte Kategorien deaktivieren
    if (consent && consent.statistics) {
      loadAnalytics();
    } else if (consent) {
      (window as any).__umami_disabled = true;
    }
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent(true, true);
    setVisible(false);
  };

  const acceptNecessary = () => {
    saveConsent(false, false);
    setVisible(false);
  };

  const acceptCustom = () => {
    saveConsent(statistics, comfort);
    setVisible(false);
  };

  return (
    <div className="immozwerg-cookie-banner fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
      <div className="immozwerg-cookie-banner-card max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl border border-[#e8dff0] overflow-hidden">
        {/* Header */}
        <div className="immozwerg-cookie-header flex items-start gap-3 p-5 pb-3">
          <div className="immozwerg-cookie-icon flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}>
            <Cookie size={16} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-[#2d1b4e] text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
              Diese Website verwendet Cookies
            </h3>
            <p className="immozwerg-cookie-description text-xs text-[#6b5c7e] mt-1 leading-relaxed">
              Wir setzen Cookies ein, um die Website zu betreiben und die Nutzung zu analysieren. Technisch notwendige Cookies sind immer aktiv.{" "}
              <Link href="/datenschutz" className="text-[#631172] font-semibold underline underline-offset-2 decoration-1 hover:no-underline">Datenschutzerklärung</Link>
            </p>
          </div>
          <button
            onClick={acceptNecessary}
            className="flex-shrink-0 text-[#b09ec0] hover:text-[#631172] transition-colors"
            title="Nur notwendige Cookies"
          >
            <X size={18} />
          </button>
        </div>

        {/* Details ausklappbar */}
        <div className="immozwerg-cookie-details-toggle px-5 pb-1">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-xs text-[#631172] hover:text-[#a066c2] transition-colors font-medium"
          >
            {showDetails ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {showDetails ? "Details ausblenden" : "Details anzeigen"}
          </button>
        </div>

        {showDetails && (
          <div className="px-5 py-3 space-y-3 border-t border-[#f0e8f8] mt-2">
            {/* Notwendig */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-[#2d1b4e]">Notwendige Cookies</p>
                <p className="text-xs text-[#6b5c7e] mt-0.5">Session-Verwaltung, Favoriten-Speicherung (localStorage). Ohne diese Cookies funktioniert die Website nicht.</p>
              </div>
              <div className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-[#f0e8f8] text-[#631172]">Immer aktiv</div>
            </div>
            {/* Statistik */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-[#2d1b4e]">Statistik (Umami Analytics)</p>
                <p className="text-xs text-[#6b5c7e] mt-0.5">Anonyme Seitenaufruf-Statistiken. Keine personenbezogenen Daten, kein Cross-Site-Tracking. Betrieben auf eigenem Server.</p>
              </div>
              <label className="flex-shrink-0 relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={statistics} onChange={e => setStatistics(e.target.checked)} className="sr-only peer" />
                <div className="w-9 h-5 bg-[#e8dff0] peer-checked:bg-[#a066c2] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
              </label>
            </div>
            {/* Komfort */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-[#2d1b4e]">Komfort (Schriftarten)</p>
                <p className="text-xs text-[#6b5c7e] mt-0.5">Die Schriftarten werden lokal über die Website ausgeliefert. Es findet keine Übertragung Ihrer IP-Adresse an Google Fonts statt.</p>
              </div>
              <label className="flex-shrink-0 relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={comfort} onChange={e => setComfort(e.target.checked)} className="sr-only peer" />
                <div className="w-9 h-5 bg-[#e8dff0] peer-checked:bg-[#a066c2] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
              </label>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="immozwerg-cookie-actions flex flex-col sm:flex-row gap-2 p-5 pt-3">
          <button
            onClick={acceptAll}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02]"
            style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}
          >
            Alle akzeptieren
          </button>
          {showDetails ? (
            <button
              onClick={acceptCustom}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-[#631172] border border-[#a066c2] bg-transparent hover:bg-[#f5eeff] transition-all"
            >
              Auswahl speichern
            </button>
          ) : (
            <button
              onClick={acceptNecessary}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-[#631172] border border-[#a066c2] bg-transparent hover:bg-[#f5eeff] transition-all"
            >
              Nur notwendige
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
